import React, { useEffect, useMemo, useState } from 'react';
import { IPartnerRating } from '../../interfaces';
import RatingDialogLayout, { ratingCardId } from '../common/RatingDialogLayout';
import RatingPaneHeader from '../common/RatingPaneHeader';
import RatingRail, { IRatingRailItem } from '../common/RatingRail';
import { formatDate, formatRatingPeriod, pluralize } from '../common/ratingUtils';
import { useRatingToast } from '../common/useRatingToast';
import PartnerCriterionCard from './PartnerCriterionCard';
import {
  buildPartnerRequests,
  criterionKey,
  CriterionValue,
  entryOf,
  filledCount,
  firstUnfilled,
  groupPartnerRatings,
  IPartnerGroup,
  overrideCount,
  PartnerRatingState,
  remainingCount,
  resetOverrides,
  restoreEntry,
  setBase,
  setOverride,
} from './partnerRatingState';
import { useSavePartnerRating } from './useSavePartnerRating';

const RATING_FORMS: [string, string, string] = ['оценка', 'оценки', 'оценок'];
const PARTNER_FORMS: [string, string, string] = ['партнеру', 'партнерам', 'партнерам'];

const partnerMeta = (group: IPartnerGroup) =>
  [
    group.serviceTypeName,
    group.positionName,
    group.contractEnd ? `договор до ${formatDate(group.contractEnd)}` : null,
    `работает на ${group.realEstates.length} ЖК`,
  ]
    .filter(Boolean)
    .join(' · ');

const railItem = (group: IPartnerGroup, state: PartnerRatingState): IRatingRailItem => ({
  key: group.key,
  title: group.partnerName,
  subtitle: [group.serviceTypeName, group.positionName].filter(Boolean).join(' · '),
  chips: group.realEstates.map(({ realEstateName }) => realEstateName),
  filled: filledCount(group, state),
  total: group.criteria.length,
});

interface IProps {
  open: boolean;
  partners: IPartnerRating[];
  onClose(): void;
  onSaveRating(): void;
}

const SavePartnerRatingDialog = ({ open, partners, onClose, onSaveRating }: IProps) => {
  const groups = useMemo(() => groupPartnerRatings(partners), [partners]);
  const [state, setState] = useState<PartnerRatingState>({});
  const [current, setCurrent] = useState(0);
  const [sentList, setSentList] = useState<IPartnerRating[] | null>(null);
  const [flashKey, setFlashKey] = useState<string | null>(null);
  const [openRealEstates, setOpenRealEstates] = useState<Record<string, boolean>>({});
  const { saveRating, loading } = useSavePartnerRating();
  const toast = useRatingToast();

  const index = Math.min(current, Math.max(groups.length - 1, 0));
  const group = groups[index];
  const remaining = remainingCount(groups, state);
  const period = formatRatingPeriod(partners[0]?.dateAt);

  useEffect(() => {
    if (flashKey) document.getElementById(ratingCardId(flashKey))?.scrollIntoView({ block: 'center' });
  }, [flashKey, index]);

  const select = (next: number) => {
    setCurrent(Math.max(0, Math.min(groups.length - 1, next)));
    setFlashKey(null);
  };

  const handleBase = (key: string, value: CriterionValue) => {
    const previous = entryOf(state, key);
    setState((prev) => setBase(prev, key, value));
    setFlashKey(null);
    if (overrideCount(previous) > 0) {
      toast(`Общая оценка изменена. Уточнения по ЖК сброшены (${overrideCount(previous)}).`, () =>
        setState((prev) => restoreEntry(prev, key, previous)),
      );
    }
  };

  const handleOverride = (key: string, realEstateGuid: string, value: CriterionValue) => {
    if (entryOf(state, key).base === null) {
      toast('Сначала поставьте общую оценку по критерию — уточнения считаются от неё.');
      return;
    }
    setState((prev) => setOverride(prev, key, realEstateGuid, value));
  };

  const handleReset = (key: string) => {
    const previous = entryOf(state, key);
    setState((prev) => resetOverrides(prev, key));
    toast(`Уточнения по ЖК сброшены (${overrideCount(previous)}).`, () =>
      setState((prev) => restoreEntry(prev, key, previous)),
    );
  };

  const toggleRealEstates = (key: string) => setOpenRealEstates((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSubmit = async () => {
    const missing = firstUnfilled(groups, state);
    if (missing) {
      setCurrent(missing.groupIndex);
      setFlashKey(missing.key);
      toast(`Не заполнено ${remaining} ${pluralize(remaining, RATING_FORMS)}. Перешли к первому незаполненному.`);
      return;
    }
    const saved = await saveRating(buildPartnerRequests(groups, state));
    if (saved) {
      setSentList(partners);
      toast(`Оценки по ${groups.length} ${pluralize(groups.length, PARTNER_FORMS)} отправлены. Период: ${period}.`);
    }
    // После ошибки тоже перезапрашиваем: строку мог оценить другой пользователь
    onSaveRating();
  };

  if (!group) return null;

  return (
    <RatingDialogLayout
      open={open}
      title="Оценка партнеров"
      subtitle={`Период: ${period} · оценка ставится один раз по партнеру и применяется ко всем его ЖК`}
      rail={<RatingRail items={groups.map((item) => railItem(item, state))} activeIndex={index} onSelect={select} />}
      footerText="Оценка применяется ко всем ЖК партнера. Уточнения по отдельным ЖК пересчитывают общую оценку."
      submitLabel="Оценить партнеров"
      remaining={remaining}
      loading={loading || sentList === partners}
      onSubmit={handleSubmit}
      onClose={onClose}
    >
      <RatingPaneHeader
        index={index}
        total={groups.length}
        title={group.partnerName}
        meta={partnerMeta(group)}
        onPrev={() => select(index - 1)}
        onNext={() => select(index + 1)}
      />

      {group.criteria.map((criterion) => {
        const key = criterionKey(group, criterion);
        return (
          <PartnerCriterionCard
            key={key}
            id={ratingCardId(key)}
            group={group}
            criterion={criterion}
            entry={entryOf(state, key)}
            flash={flashKey === key}
            realEstatesOpen={Boolean(openRealEstates[key])}
            onToggleRealEstates={() => toggleRealEstates(key)}
            onBase={(value) => handleBase(key, value)}
            onOverride={(realEstateGuid, value) => handleOverride(key, realEstateGuid, value)}
            onResetOverrides={() => handleReset(key)}
          />
        );
      })}
    </RatingDialogLayout>
  );
};

export default SavePartnerRatingDialog;
